import type {
  QueryListResponse,
  RecentLogsResponse,
  TelemetryLogRow,
  TelemetryRuntimeConfig,
  TelemetryTraceRow,
  TraceBundleResponse,
} from "@/lib/types";

const makeHeaders = (token: string | null): HeadersInit => {
  const headers: Record<string, string> = {
    Accept: "application/json",
  };
  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }
  return headers;
};

const assertOk = async <T>(response: Response): Promise<T> => {
  const payload = (await response.json()) as T & { error?: string };
  if (!response.ok) {
    const message =
      typeof (payload as { error?: unknown }).error === "string"
        ? (payload as { error: string }).error
        : `Request failed (${response.status})`;
    throw new Error(message);
  }
  return payload;
};

const buildOrgUrl = (config: TelemetryRuntimeConfig, path: string, query?: URLSearchParams): string => {
  const base = `${config.telemetryOrigin}/${encodeURIComponent(config.org)}${path}`;
  const serialized = query?.toString();
  return serialized ? `${base}?${serialized}` : base;
};

export const fetchLogsPage = async (
  config: TelemetryRuntimeConfig,
  page: number,
  pageSize: number,
): Promise<QueryListResponse<TelemetryLogRow>> => {
  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const response = await fetch(buildOrgUrl(config, "/query/v1/logs", query), {
    method: "GET",
    credentials: "include",
    headers: makeHeaders(config.authToken),
  });
  return assertOk<QueryListResponse<TelemetryLogRow>>(response);
};

export const fetchTracesPage = async (
  config: TelemetryRuntimeConfig,
  page: number,
  pageSize: number,
): Promise<QueryListResponse<TelemetryTraceRow>> => {
  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  const response = await fetch(buildOrgUrl(config, "/query/v1/traces", query), {
    method: "GET",
    credentials: "include",
    headers: makeHeaders(config.authToken),
  });
  return assertOk<QueryListResponse<TelemetryTraceRow>>(response);
};

export const fetchTraceBundle = async (
  config: TelemetryRuntimeConfig,
  traceId: string,
): Promise<TraceBundleResponse> => {
  const response = await fetch(
    buildOrgUrl(config, `/query/v1/trace/${encodeURIComponent(traceId)}`),
    {
      method: "GET",
      credentials: "include",
      headers: makeHeaders(config.authToken),
    },
  );
  return assertOk<TraceBundleResponse>(response);
};

export const fetchRecentLogs = async (
  config: TelemetryRuntimeConfig,
  windowSeconds: number,
  limit: number,
): Promise<RecentLogsResponse> => {
  const query = new URLSearchParams({
    format: "json",
    seconds: String(windowSeconds),
    limit: String(limit),
  });
  const response = await fetch(buildOrgUrl(config, "/logs", query), {
    method: "GET",
    credentials: "include",
    headers: makeHeaders(config.authToken),
  });
  return assertOk<RecentLogsResponse>(response);
};
